import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { post } from '@/api/client';
import { useAuthStore } from '@/store/authStore';
import { storage } from '@/lib/storage';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { toast } from '@/store/toastStore';

/**
 * 테스트 유저 로그인 — 소셜 없이 닉네임으로 로그인.
 *  - 관리자 비밀번호(TEST_LOGIN_PASSWORD) 입력 필수.
 *  - 미리 어드민(테스트 관리)에서 생성한 is_test_user 계정만 로그인 가능.
 */
export function TestLoginPage() {
  const navigate = useNavigate();
  const { setMe } = useAuthStore();
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!nickname.trim() || !password) {
      toast.error('닉네임과 관리자 비밀번호를 입력하세요.');
      return;
    }
    setLoading(true);
    try {
      const data = await post<{
        accessToken: string;
        user: {
          userId: string;
          nickname: string;
          level: number;
          isPremium: boolean;
          onboardingDone: boolean;
          profileImageUrl: string | null;
          adminRole: string | null;
        };
      }>('/auth/test-login', { nickname: nickname.trim(), password });

      storage.set('accessToken', data.accessToken);
      setMe({
        userId: data.user.userId,
        nickname: data.user.nickname,
        level: data.user.level,
        score: 0,
        isPremium: data.user.isPremium,
        adminRole: data.user.adminRole ?? null,
        profileImageUrl: data.user.profileImageUrl,
        onboardingDone: true,
      });
      toast(`${data.user.nickname} 테스트 계정으로 로그인했어요.`, 'success');
      navigate('/', { replace: true });
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message ?? '로그인에 실패했어요.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--color-bg)] px-4">
      <div className="w-full max-w-sm rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-6">
        <h1 className="mb-1 text-xl font-bold">테스트 로그인</h1>
        <p className="mb-5 text-sm text-[var(--color-text-sub)]">
          테스트 계정 닉네임과 관리자 비밀번호로 로그인합니다.
        </p>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <Input
            placeholder="테스트 계정 닉네임"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            autoComplete="off"
          />
          <Input
            type="password"
            placeholder="관리자 비밀번호"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
          <Button type="submit" disabled={loading}>
            {loading ? '로그인 중…' : '로그인'}
          </Button>
        </form>
        <p className="mt-4 text-xs text-[var(--color-text-sub)]">
          ※ 테스트 활동은 포인트가 지급되지 않으며, 모든 데이터는 어드민에서 일괄 삭제할 수 있습니다.
        </p>
      </div>
    </div>
  );
}
